import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Fab,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  PhotoAlbum as AlbumIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Collections as CollectionsIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import {
  getAllAlbums,
  createAlbum,
  updateAlbum,
  deleteAlbum,
  getThumbnailUrl
} from '../services/api';

const MotionCard = motion(Card);

const Albums = () => {
  const navigate = useNavigate();
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [newAlbum, setNewAlbum] = useState({ name: '', description: '' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuAlbum, setMenuAlbum] = useState(null);

  useEffect(() => {
    loadAlbums();
  }, []);

  const loadAlbums = async () => {
    try {
      setLoading(true);
      const response = await getAllAlbums();
      console.log('Albums response:', response); // Debug log
      setAlbums(response.albums || []);
      setError(null);
    } catch (err) {
      console.error('Error loading albums:', err);
      console.error('Error details:', err.response?.data || err.message);
      setError('Failed to load albums. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAlbum = async () => {
    if (!newAlbum.name.trim()) return;

    try {
      await createAlbum(newAlbum.name, newAlbum.description);
      setCreateDialogOpen(false);
      setNewAlbum({ name: '', description: '' });
      loadAlbums();
    } catch (err) {
      console.error('Error creating album:', err);
      setError('Failed to create album. Please try again.');
    }
  };

  const handleUpdateAlbum = async () => {
    if (!selectedAlbum || !selectedAlbum.name.trim()) return;

    try {
      await updateAlbum(selectedAlbum.id, {
        name: selectedAlbum.name,
        description: selectedAlbum.description,
      });
      setEditDialogOpen(false);
      setSelectedAlbum(null);
      loadAlbums();
    } catch (err) {
      console.error('Error updating album:', err);
      setError('Failed to update album. Please try again.');
    }
  };

  const handleDeleteAlbum = async (albumId) => {
    if (window.confirm('Are you sure you want to delete this album? The images will remain in your gallery.')) {
      try {
        await deleteAlbum(albumId);
        loadAlbums();
      } catch (err) {
        console.error('Error deleting album:', err);
        setError('Failed to delete album. Please try again.');
      }
    }
  };

  const handleMenuOpen = (event, album) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuAlbum(album);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuAlbum(null);
  };

  const handleEditClick = () => {
    setSelectedAlbum(menuAlbum);
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    handleDeleteAlbum(menuAlbum.id);
    handleMenuClose();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading && albums.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress size={60} sx={{ color: '#ffffff' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography
            variant="h4"
            sx={{
              mb: 1,
              color: '#ffffff',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <AlbumIcon /> Albums
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
            }}
          >
            Organize your photos into collections
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{
            backgroundColor: '#667eea',
            '&:hover': {
              backgroundColor: '#764ba2',
            },
          }}
        >
          Create Album
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {albums.length === 0 ? (
        <Card
          sx={{
            p: 6,
            textAlign: 'center',
            backgroundColor: '#161616',
            border: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <CollectionsIcon sx={{ fontSize: 64, color: 'rgba(255, 255, 255, 0.3)', mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.9)' }}>
            No Albums Yet
          </Typography>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 3 }}>
            Create your first album to start organizing your photos
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{
              backgroundColor: '#667eea',
              '&:hover': {
                backgroundColor: '#764ba2',
              },
            }}
          >
            Create Your First Album
          </Button>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {albums.map((album, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={album.id}>
              <MotionCard
                whileHover={{
                  scale: 1.03,
                  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                }}
                whileTap={{ scale: 0.98 }}
                sx={{
                  cursor: 'pointer',
                  background: 'rgba(26, 26, 46, 0.8)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  position: 'relative',
                  overflow: 'hidden',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                onClick={() => navigate(`/albums/${album.id}`)}
              >
                {album.cover_image_url ? (
                  <CardMedia
                    component="img"
                    height="200"
                    image={album.cover_image_url}
                    alt={album.name}
                    sx={{
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 200,
                      backgroundColor: 'rgba(102, 126, 234, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <AlbumIcon sx={{ fontSize: 64, color: 'rgba(102, 126, 234, 0.3)' }} />
                  </Box>
                )}

                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#ffffff',
                        fontWeight: 600,
                        flexGrow: 1,
                      }}
                    >
                      {album.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, album)}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.6)',
                        '&:hover': { color: '#ffffff' }
                      }}
                    >
                      <MoreIcon />
                    </IconButton>
                  </Box>

                  {album.description && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        mb: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {album.description}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Chip
                      label={`${album.image_count} ${album.image_count === 1 ? 'image' : 'images'}`}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(102, 126, 234, 0.2)',
                        color: 'rgba(255, 255, 255, 0.8)',
                        border: '1px solid rgba(102, 126, 234, 0.3)',
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                    >
                      {formatDate(album.created_at)}
                    </Typography>
                  </Box>
                </CardContent>
              </MotionCard>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Menu for album actions */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(26, 26, 46, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }
        }}
      >
        <MenuItem onClick={handleEditClick}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit Album
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: '#f44336' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete Album
        </MenuItem>
      </Menu>

      {/* Create Album Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: '#1a1a2e',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }
        }}
      >
        <DialogTitle sx={{ color: '#ffffff' }}>Create New Album</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Album Name"
            fullWidth
            variant="outlined"
            value={newAlbum.name}
            onChange={(e) => setNewAlbum({ ...newAlbum, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description (optional)"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={newAlbum.description}
            onChange={(e) => setNewAlbum({ ...newAlbum, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateAlbum}
            variant="contained"
            disabled={!newAlbum.name.trim()}
            sx={{
              backgroundColor: '#667eea',
              '&:hover': {
                backgroundColor: '#764ba2',
              },
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Album Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: '#1a1a2e',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }
        }}
      >
        <DialogTitle sx={{ color: '#ffffff' }}>Edit Album</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Album Name"
            fullWidth
            variant="outlined"
            value={selectedAlbum?.name || ''}
            onChange={(e) => setSelectedAlbum({ ...selectedAlbum, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description (optional)"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={selectedAlbum?.description || ''}
            onChange={(e) => setSelectedAlbum({ ...selectedAlbum, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateAlbum}
            variant="contained"
            disabled={!selectedAlbum?.name.trim()}
            sx={{
              backgroundColor: '#667eea',
              '&:hover': {
                backgroundColor: '#764ba2',
              },
            }}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Albums;